/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Typography, List, Skeleton, Stack, Grid, Chip, useTheme } from '@mui/material';
import { useObjectContext } from '@/hooks/useObjectContext';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useDeleteNoteMutation } from '@/features/engagements/engagementsApi';
import { formatDate } from '@/utils/constants';
import {
	STORAGE_PURPOSES,
	clientStoragePath,
	dealStoragePath,
	relationshipCompanyStoragePath,
} from '@/utils/storagePaths';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { useLocation } from 'react-router-dom';
import EngagementsAccordion from '../EngagementsAccordion';
import AttachmentListItem from '@/components/AttachmentListItem';
import { useSelector } from 'react-redux';
import { useGetNoteFilesQuery } from '@/features/files/filesApi';
import BrokerAvatar from '@/components/BrokerAvatar';
import DealInfo from '../EngagementsAccordion/DealInfo';
import { safelyParseDate } from '@/utils/date';
import { noteFolders } from '@/utils/folders';
import SafeHtmlContent from '../SafeHtmlContent';

export const NoteAccordion = ({ note, expandedByDefault = true }) => {
	const theme = useTheme();
	const {
		dealRow,
		clientId,
		dealId,
		relationshipCompanyId,
		isClientView: isClient,
		isRelationshipCompanyView,
	} = useObjectContext();
	const ownerUserId = note.createdBy ?? note.ownerDocId;
	const { data: owner, ...ownerQuery } = useGetUserDetailsQuery({ userId: ownerUserId }, {
		skip: !ownerUserId,
	});

	const ownerName = !ownerQuery?.isSuccess ? '' : (owner?.name ?? owner?.email ?? 'None');

	// Company notes sit under the company. Deal notes sit under the deal. Client notes sit
	// under the client. In a deal view, wait for the deal row so the deal's client is known.
	const noteClientId = note.clientId ?? clientId;
	const folderPath = (() => {
		const companyId =
			note.relationshipCompanyId ??
			(isRelationshipCompanyView ? relationshipCompanyId : null);
		if (companyId) {
			return relationshipCompanyStoragePath(companyId, STORAGE_PURPOSES.attachments);
		}
		if (note.dealId) {
			if (!isClient && !dealRow) return null;
			return dealStoragePath(
				{ clientDocId: isClient ? clientId : dealRow.clientDocId, dealDocId: note.dealId },
				STORAGE_PURPOSES.attachments,
			);
		}
		return noteClientId && clientStoragePath(noteClientId, STORAGE_PURPOSES.attachments);
	})();

	const { data, isFetching, isUninitialized, isError, ...noteFilesQuery } = useGetNoteFilesQuery(
		{
			noteId: note.id,
			folderPath,
		},
		{ skip: !note.id || !folderPath },
	);

	const files = useMemo(
		() => ((data ?? []).length > (note?.files ?? []).length ? data : note.files) ?? [],
		[note.files, data],
	);

	const { refreshIndicator } = useSelector((state) => state.attachmentsTable);

	useEffect(() => {
		if (data) {
			noteFilesQuery.refetch();
		}
	}, [refreshIndicator]);

	const [deleteNote, { isLoading }] = useDeleteNoteMutation();
	const [dialogOpen, setDialogOpen] = useState(false);

	const handleOpenDialog = () => setDialogOpen(true);
	const handleCloseDialog = () => setDialogOpen(false);

	const handleConfirmDialog = async () => {
		handleCloseDialog();
		await archiveNote();
	};

	const archiveNote = async () => {
		await deleteNote({
			...(isClient ? { clientId } : { dealId }),
			noteId: note.id,
			objectType: isClient ? 'client' : dealRow?.objectType,
		});
	};

	// const getFullName = () => {
	// 	return owner ? `${owner?.firstName} ${owner?.lastName}` : 'Unknown';
	// };

	const date = useMemo(() => safelyParseDate(note.timestamp), [note.timestamp]);
	const lastUpdated = useMemo(() => safelyParseDate(note.lastUpdated), [note.lastUpdated]);

	const location = useLocation();

	const noteOpened = useMemo(() => {
		if (location.state?.note) {
			return location.state.note === note?.id;
		}
		return false;
	}, [location?.state?.note, note?.id]);
	const noteParams = useMemo(
		() => ({
			...((isFetching || isUninitialized || isError) && {
				footerContent: isError ? (
					<Typography variant='task_updated' sx={{ color: theme.palette.error.main }}>
						{'Error loading attachments'}
					</Typography>
				) : (
					<Skeleton variant='text' width='25%' height='14px' />
				),
			}),
			...((files ?? []).length > 0 && {
				expandedContent: (
					<>
						<List
							dense
							sx={{
								width: '100%',
								'& .MuiListItemIcon-root': {
									minWidth: 0,
									paddingRight: '0.25em',
								},
								'& .MuiSvgIcon-root': {
									fontSize: 18,
								},
							}}
						>
							{files.map((file, i) => (
								<AttachmentListItem
									key={`attachment-${file?.md5Hash ?? file?.id}-${i}`}
									{...(file?.md5Hash ? { file } : { fileId: file.id })}
								/>
							))}
						</List>
					</>
				),
				footerContent: (
					<Typography variant='task_updated'>{`${
						(files ?? []).length
					} attachments`}</Typography>
				),
			}),
		}),
		[note?.id, files, isFetching, isUninitialized, isError],
	);

	const associatedDealId = note?.dealId;

	const folderName = note?.folderName ?? 'General';
	const isGeneral = noteFolders.includes(folderName) || folderName === 'General';

	const NoteContent = useCallback(() => {
		const html = note.htmlBody ?? '';
		if (!html) return <></>;
		return <SafeHtmlContent html={html} />;
	}, [note.htmlBody]);
	return (
		<>
			<EngagementsAccordion
				title={
					<Grid container spacing={'0.5em'} alignItems='center'>
						<span>
							<span style={{ fontWeight: 'bold' }}>Note</span> by
						</span>
						<Stack direction='row' alignItems='center'>
							{owner && (
								<BrokerAvatar
									broker={owner}
									width='1em'
									height='1em'
									sx={{ marginRight: '0.25em !important' }}
								/>
							)}
							{ownerName}
						</Stack>
						<span>{' | '}</span>
						<span style={{ fontWeight: '500' }}>{formatDate(date, true)}</span>

						{isClient && associatedDealId && (
							<>
								<span>{' | '}</span>
								<DealInfo dealId={associatedDealId} />
							</>
						)}

						{folderName && (
							<Chip
								size='small'
								label={isGeneral ? 'General' : folderName}
								variant='outlined'
								color='primary'
								sx={{
									height: '22px',
									fontSize: '0.75rem',
									borderColor: 'rgba(80, 90, 252, 0.3)',
									color: theme.palette.primary.main,
									...(theme.palette.mode !== 'dark' && {
										backgroundColor: 'rgba(80, 90, 252, 0.06)',
									}),
									'& .MuiChip-label': {
										px: '0.75em',
									},
								}}
							/>
						)}
					</Grid>
				}
				expandedByDefault={noteOpened || expandedByDefault}
				previewWhenCollapsed={false}
				// lastUpdated={lastUpdated}
				loading={isLoading}
				handleDelete={handleOpenDialog}
				content={<NoteContent />}
				{...noteParams}
			/>
			<ConfirmDialog
				openDialog={dialogOpen}
				handleClose={handleCloseDialog}
				handleConfirm={handleConfirmDialog}
				actionName={'delete'}
			/>
		</>
	);
};
NoteAccordion.displayName = 'NoteAccordion';
